import { useEffect, useRef, useState } from 'react'
import type { MessageFile } from '../api/types.ts'
import { VoicePlayer } from './VoicePlayer.tsx'

/**
 * Вложения внутри сообщения.
 *
 * Картинки показываются сеткой, остальное — строкой с именем и размером.
 * Ключ доступа к файлам лежит в защищённой куке, поэтому обычный <img>
 * работает как есть: ни в адресе, ни в разметке секретов нет.
 */

const ICONS: Record<string, string> = {
  'application/pdf': '📄',
  'application/msword': '📝',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '📝',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '📊',
  'text/plain': '📃',
}

function sizeText(value: number): string {
  if (value < 1024) return `${value} Б`
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} КБ`
  return `${(value / 1024 / 1024).toFixed(1)} МБ`
}

export function MessageFiles({ files, own }: { files: MessageFile[]; own: boolean }) {
  /** Какая картинка открыта во весь экран: храним номер, чтобы листать */
  const [zoomedAt, setZoomedAt] = useState<number | null>(null)

  if (files.length === 0) return null

  /**
   * Раскладываем по видам, а не по типам содержимого.
   *
   * Раньше здесь гадали по «mimeType», и гадали неверно: любое видео
   * показывалось кружком — даже присланный из галереи ролик на десять
   * минут, — а любой звуковой файл превращался в голосовое с нарисованной
   * из ничего полоской громкости. Вид приходит с сервера и решается
   * при отправке.
   */
  const photos = files.filter((f) => f.kind === 'PHOTO')
  const videos = files.filter((f) => f.kind === 'VIDEO')
  const rounds = files.filter((f) => f.kind === 'ROUND')
  const voices = files.filter((f) => f.kind === 'VOICE')
  const audios = files.filter((f) => f.kind === 'AUDIO')
  const documents = files.filter((f) => f.kind === 'FILE')

  return (
    <>
      {photos.length > 0 && (
        <div className={'files-grid files-grid-' + Math.min(photos.length, 4)}>
          {photos.map((file) => (
            <button
              key={file.id}
              className="file-image"
              onClick={() => setZoomedAt(photos.indexOf(file))}
              aria-label={`Открыть ${file.fileName}`}
              /* Место под картинку резервируется по её пропорциям,
                 иначе лента дёргается по мере подгрузки */
              style={
                file.width && file.height && photos.length === 1
                  ? { aspectRatio: `${file.width} / ${file.height}` }
                  : undefined
              }
            >
              <img src={file.thumbUrl ?? ''} alt={file.fileName} loading="lazy" />
            </button>
          ))}
        </div>
      )}

      {/* Кружок: без рамки и без полоски, как его и записывали */}
      {rounds.map((file) => (
        <video
          key={file.id}
          className="video-circle"
          src={file.url}
          controls
          playsInline
          preload="metadata"
        />
      ))}

      {/* Обычное видео: прямоугольником, с полоской и во весь экран */}
      {videos.map((file) => (
        <video
          key={file.id}
          className="file-video"
          src={file.url}
          controls
          playsInline
          preload="metadata"
          style={
            file.width && file.height
              ? { aspectRatio: `${file.width} / ${file.height}` }
              : undefined
          }
        />
      ))}

      {voices.map((file) => (
        <VoicePlayer key={file.id} file={file} own={own} />
      ))}

      {/* Музыка и звукозаписи файлом: имя, размер и обычный проигрыватель.
          Полоску громкости здесь рисовать не из чего — её считают
          при записи, а у присланного файла её нет */}
      {audios.map((file) => (
        <div key={file.id} className="file-audio">
          <div className="file-audio-head">
            <span className="file-doc-name">{file.fileName}</span>
            <span className="file-doc-size">{sizeText(file.sizeBytes)}</span>
          </div>
          <audio src={file.url} controls preload="metadata" />
        </div>
      ))}

      {documents.map((file) => (
        <a key={file.id} className="file-doc" href={file.url} download={file.fileName}>
          <span className="file-doc-icon" aria-hidden="true">
            {ICONS[file.mimeType] ?? '📎'}
          </span>
          <span className="file-doc-text">
            <span className="file-doc-name">{file.fileName}</span>
            <span className="file-doc-size">{sizeText(file.sizeBytes)}</span>
          </span>
        </a>
      ))}

      {zoomedAt !== null && photos[zoomedAt] && (
        <Lightbox
          images={photos}
          at={zoomedAt}
          onMove={setZoomedAt}
          onClose={() => setZoomedAt(null)}
        />
      )}
    </>
  )
}

/**
 * Просмотр во весь экран с перелистыванием.
 *
 * Стрелки на клавиатуре и свайп на телефоне: когда в сообщении несколько
 * снимков, закрывать и открывать каждый по отдельности — мучение.
 */
function Lightbox({
  images,
  at,
  onMove,
  onClose,
}: {
  images: MessageFile[]
  at: number
  onMove: (index: number) => void
  onClose: () => void
}) {
  const current = images[at]
  const many = images.length > 1

  const go = (step: number) => {
    // По кругу: с последнего на первый. Тупик в конце ленты раздражает
    onMove((at + step + images.length) % images.length)
  }

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
      if (!many) return
      if (event.key === 'ArrowLeft') go(-1)
      if (event.key === 'ArrowRight') go(1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  // Обычный объект здесь не годится: он пересоздаётся при каждой
  // перерисовке, и точка начала свайпа теряется между нажатием и отпусканием
  const swipeFrom = useRef(0)

  if (!current) return null

  return (
    <div
      className="lightbox"
      onClick={onClose}
      role="dialog"
      aria-label={current.fileName}
      onPointerDown={(e) => {
        swipeFrom.current = e.clientX
      }}
      onPointerUp={(e) => {
        const shift = e.clientX - swipeFrom.current
        if (many && Math.abs(shift) > 60) go(shift < 0 ? 1 : -1)
      }}
    >
      <img src={current.url} alt={current.fileName} />

      {many && (
        <>
          <button
            className="lightbox-arrow is-left"
            onClick={(e) => {
              e.stopPropagation()
              go(-1)
            }}
            aria-label="Предыдущая"
          >
            ‹
          </button>
          <button
            className="lightbox-arrow is-right"
            onClick={(e) => {
              e.stopPropagation()
              go(1)
            }}
            aria-label="Следующая"
          >
            ›
          </button>
          <span className="lightbox-count">
            {at + 1} из {images.length}
          </span>
        </>
      )}

      <a
        className="lightbox-download"
        href={current.url}
        download={current.fileName}
        onClick={(e) => e.stopPropagation()}
      >
        Скачать
      </a>
      <button className="lightbox-close" aria-label="Закрыть">
        ✕
      </button>
    </div>
  )
}
